import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LinkButton } from "../components/ui";
import { APP_URL } from "../lib/env";

const PRIMARY_NAV: Array<{ label: string; to?: string; href?: string; children?: Array<{ label: string; to: string; desc?: string }> }> = [
  {
    label: "Platform",
    to: "/platform",
    children: [
      { label: "Platform overview", to: "/platform", desc: "End-to-end workflow + architecture" },
      { label: "Features", to: "/features", desc: "Clinical engine · synthesis · bundles · QBO" },
    ],
  },
  {
    label: "Solutions",
    children: [
      { label: "Long-Term Care (SNF)", to: "/solutions/snf", desc: "ASCP · F757 / F758 / F881" },
      { label: "Ambulatory Surgical (ASC)", to: "/solutions/asc", desc: "AAAHC ch 11 · DEA · vaccine temp" },
      { label: "Dialysis (ESRD)", to: "/solutions/dialysis", desc: "CMS ESRD CfC · AAMI water" },
    ],
  },
  { label: "Pricing", to: "/pricing" },
  {
    label: "Resources",
    children: [
      { label: "Resource hub", to: "/resources" },
      { label: "Blog", to: "/blog" },
      { label: "Docs", to: "/docs" },
      { label: "Trust center", to: "/trust" },
    ],
  },
  { label: "About", to: "/about" },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-40 bg-canvas/90 backdrop-blur supports-[backdrop-filter]:bg-canvas/75 border-b border-border">
      <div className="mx-auto max-w-container px-6 md:px-8">
        <div className="flex h-16 items-center justify-between gap-6">
          {/* Logo */}
          <Link
            to="/"
            aria-label="WeConsult Rx home"
            className="flex items-center"
            onClick={() => setMobileOpen(false)}
          >
            <img
              src="/logo-horizontal-no-tagline.svg"
              alt="WeConsult Rx"
              width="220"
              height="48"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-1">
            {PRIMARY_NAV.map((item) => {
              if (item.children) {
                const isOpen = openMenu === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(item.label)}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-ink-700 hover:text-forest-700 rounded-md"
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      onFocus={() => setOpenMenu(item.label)}
                      onClick={() => setOpenMenu(isOpen ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown className="h-4 w-4" aria-hidden />
                    </button>
                    {isOpen ? (
                      <div
                        role="menu"
                        className="absolute left-0 top-full mt-1 w-72 rounded-xl bg-surface border border-border shadow-pop p-2"
                      >
                        {item.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            role="menuitem"
                            onClick={() => setOpenMenu(null)}
                            className={({ isActive }) =>
                              `block rounded-lg px-3 py-2.5 hover:bg-forest-50 ${
                                isActive ? "bg-forest-50" : ""
                              }`
                            }
                          >
                            <div className="text-sm font-semibold text-ink-900">{c.label}</div>
                            {c.desc ? <div className="text-xs text-ink-600 mt-0.5">{c.desc}</div> : null}
                          </NavLink>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.label}
                  to={item.to!}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? "text-forest-700"
                        : "text-ink-700 hover:text-forest-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* CTA + Sign In */}
          <div className="hidden lg:flex items-center gap-2">
            <LinkButton href={APP_URL} variant="ghost" size="md">
              Sign in
            </LinkButton>
            <LinkButton to="/demo" variant="primary" size="md">
              Request a demo
            </LinkButton>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-ink-700"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen ? (
        <nav
          aria-label="Mobile primary"
          className="lg:hidden border-t border-border bg-canvas"
          key={loc.pathname}
        >
          <div className="mx-auto max-w-container px-6 py-4 space-y-1">
            {PRIMARY_NAV.flatMap((i) =>
              i.children
                ? i.children.map((c) => ({ label: c.label, to: c.to }))
                : [{ label: i.label, to: i.to! }]
            ).map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive
                      ? "bg-forest-50 text-forest-700"
                      : "text-ink-700 hover:bg-ink-50"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
            <div className="pt-3 mt-3 border-t border-border flex flex-col gap-2">
              <LinkButton href={APP_URL} variant="secondary" size="md">
                Sign in
              </LinkButton>
              <LinkButton to="/demo" variant="primary" size="md">
                Request a demo
              </LinkButton>
            </div>
          </div>
        </nav>
      ) : null}
    </header>
  );
}
